import './PageBusyLoader.css';

type PageBusyLoaderProps = {
  pageName: string;
  overlay?: boolean;
  className?: string;
  /** Overrides the default "Loading {pageName}...." text */
  label?: string;
};

export function pageNameFromPath(pathname: string): string {
  if (pathname.startsWith('/calendar')) return 'Calendar';
  if (pathname.startsWith('/analytics')) return 'Analytics';
  if (pathname.startsWith('/prompts') || pathname.startsWith('/create-prompt')) {
    return 'Prompts';
  }
  if (pathname.startsWith('/messages/requests')) return 'Message Requests';
  if (pathname.startsWith('/messages/questions')) return 'Questions';
  if (pathname.startsWith('/messages/contacts')) return 'Contacts';
  if (pathname.startsWith('/messages')) return 'Message Hub';
  if (pathname.startsWith('/user-profile')) return 'Profile';
  if (pathname.startsWith('/dashboard/negotiator')) return 'Negotiation Center';
  if (pathname.startsWith('/activity') || pathname.startsWith('/dashboard/activity'))
    return 'Activity';
  if (pathname.startsWith('/dashboard/references')) return 'References';
  if (pathname.startsWith('/dashboard')) return 'Dashboard';
  if (pathname.startsWith('/user-settings')) return 'Settings';
  if (pathname.startsWith('/settings/security')) return 'Security';
  if (pathname.startsWith('/settings/privacy')) return 'Privacy';
  if (pathname.startsWith('/account/connections')) return 'Connected accounts';
  if (pathname.startsWith('/onboarding')) return 'Onboarding';
  if (pathname.startsWith('/get-started')) return 'Getting Started';
  if (pathname.startsWith('/complete-profile')) return 'Onboarding';
  if (pathname.startsWith('/create-portfolio')) return 'Portfolio';
  return 'Skip Screen';
}

/** Inline ring for buttons and compact UI. */
export function BusySpinner({
  size = 'sm',
  className = '',
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizeClass =
    size === 'lg'
      ? ''
      : size === 'md'
        ? 'page-busy-loader__spinner--md'
        : 'page-busy-loader__spinner--sm';

  return (
    <span
      className={['page-busy-loader__spinner', sizeClass, className].filter(Boolean).join(' ')}
      role="status"
      aria-hidden
    />
  );
}

/** Panel / section loader (matches Message Hub inline style). */
export function InlineBusyLoader({ label }: { label: string }) {
  return (
    <div
      className="page-busy-loader page-busy-loader--compact"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`Loading ${label}`}
    >
      <div className="page-busy-loader__spinner page-busy-loader__spinner--md" aria-hidden />
      <p className="page-busy-loader__label">Loading {label}....</p>
    </div>
  );
}

export function PageBusyLoader({
  pageName,
  overlay = false,
  className = '',
  label,
}: PageBusyLoaderProps) {
  const rootClass = ['page-busy-loader', overlay ? 'page-busy-loader--overlay' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClass}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`Loading ${pageName}`}
    >
      <div className="page-busy-loader__spinner" aria-hidden />
      <p className="page-busy-loader__label">{label ?? `Loading ${pageName}....`}</p>
    </div>
  );
}
